import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ProductsService } from "@/services/products.service";
import { toast } from "@/hooks/use-toast";
import { AxiosErrorResponse } from "@/@types/Errors";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

type FormValues = {
  offers: boolean;
  checkouts: boolean;
  funnels: boolean;
  coupons: boolean;
  pixels: boolean;
};

export function DuplicateProductModal({ productId }: { productId: string }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const { register, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      offers: false,
      checkouts: false,
      funnels: false,
      coupons: false,
      pixels: false,
    },
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { mutate: duplicateProduct } = useMutation({
    mutationFn: async (options: FormValues) =>
      ProductsService.duplicateProduct({
        productId,
        options: options,
      }),
    onSuccess: async (data) => {
      toast({
        description: t("duplicate_success"),
        variant: "success",
      });
      setIsDialogOpen(false);
      navigate(`/products/edit-product/${data.id}/geral`);
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      toast({
        variant: "error",
        description:
          error?.response.data.message || t("duplicate_error"),
      });
    },
  });

  const onSubmit = (data: FormValues) => {
    duplicateProduct(data);
  };

  const defaultValues = watch();

  return (
    <Dialog open={isDialogOpen} onOpenChange={(open) => setIsDialogOpen(open)}>
      <DialogTrigger asChild>
        <Button variant="secondary" className="w-full md:max-w-[148px]">
          {t("duplicate_product")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="mb-2">{t("duplicate_product")}?</DialogTitle>
          <DialogDescription>{t("duplicate_text")}</DialogDescription>
        </DialogHeader>
        <section className="grid gap-4 mt-2 py-4">
          <div className="flex gap-2">
            <Checkbox
              onClick={() => setValue("coupons", !defaultValues.coupons)}
              {...register("coupons")}
              id="coupons"
              name="coupons"
            />
            <label
              htmlFor="coupons"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("couponsPage.title")}
            </label>
          </div>
          <div className="flex gap-2">
            <Checkbox
              onClick={() => setValue("offers", !defaultValues.offers)}
              {...register("offers")}
              id="offers"
              name="offers"
            />
            <label
              htmlFor="offers"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("offers")}
            </label>
          </div>
        </section>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t("cancel")}</Button>
          </DialogClose>
          <Button onClick={handleSubmit(onSubmit)}>
            {t("confirm_duplication")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
